import { useEffect, useRef } from 'react'
import type { IntegrationChannelDeletedPayload } from '@/api/types/crm/crmRealtime.types'
import websocket from '@/utils/websocket'
import { isIntegrationChannelDeletedWsMessage } from '@pages/Admin/CRM/utils/crmRealtimeWebSocket'

type UseIntegrationChannelDeletedRealtimeParams = {
  enabled?: boolean
  integrationId?: number | null
  /** external_channel_id провайдера */
  externalChannelId?: string | null
  /** Внутренний PK канала (integration_channel_id) */
  integrationChannelId?: number | null
  onDeleted: (payload: IntegrationChannelDeletedPayload) => void
}

const normalizeExternalId = (value: unknown): string =>
  String(value ?? '').trim()

/**
 * WS `integration_channel_deleted` → callback, если событие относится
 * к указанному integration_id + каналу (external и/или internal id).
 */
export const useIntegrationChannelDeletedRealtime = ({
  enabled = true,
  integrationId = null,
  externalChannelId = null,
  integrationChannelId = null,
  onDeleted,
}: UseIntegrationChannelDeletedRealtimeParams): void => {
  const onDeletedRef = useRef(onDeleted)
  onDeletedRef.current = onDeleted

  const watchIntegrationId =
    integrationId != null &&
    Number.isFinite(Number(integrationId)) &&
    Number(integrationId) > 0
      ? Math.floor(Number(integrationId))
      : null

  const watchExternalId = normalizeExternalId(externalChannelId) || null
  const watchChannelPk =
    integrationChannelId != null &&
    Number.isFinite(Number(integrationChannelId)) &&
    Number(integrationChannelId) > 0
      ? Math.floor(Number(integrationChannelId))
      : null

  const canWatch =
    enabled &&
    watchIntegrationId != null &&
    (watchExternalId != null || watchChannelPk != null)

  useEffect(() => {
    if (!canWatch || watchIntegrationId == null) {
      return
    }

    websocket.connect()

    const onWs = (message: unknown) => {
      if (!isIntegrationChannelDeletedWsMessage(message)) {
        return
      }

      const payload = message.data.payload
      if (Number(payload.integration_id) !== watchIntegrationId) {
        return
      }

      const eventExternalId = normalizeExternalId(payload.external_channel_id)
      const eventChannelPk = Number(payload.integration_channel_id)

      const matchesExternal =
        watchExternalId != null &&
        eventExternalId.length > 0 &&
        eventExternalId === watchExternalId
      const matchesPk =
        watchChannelPk != null &&
        Number.isFinite(eventChannelPk) &&
        eventChannelPk === watchChannelPk

      if (!matchesExternal && !matchesPk) {
        return
      }

      onDeletedRef.current(payload)
    }

    const unsubscribe = websocket.subscribe(
      'integration_channel_deleted',
      onWs,
    )

    return () => {
      unsubscribe?.()
    }
  }, [canWatch, watchChannelPk, watchExternalId, watchIntegrationId])
}
